<template>
  <div class="departments-container">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">إدارة الأقسام</span>
        <v-btn
          v-if="hasPermission('admin.departments.store')"
          v-permission="PERMISSIONS.DEPARTMENTS_CREATE"
          color="primary"
          @click="openAddDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          إضافة قسم جديد
        </v-btn>
      </v-card-title>
      
      <v-card-text>
        <!-- Search and Filter Controls -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              label="البحث في الأقسام"
              prepend-inner-icon="mdi-magnify"
              :clearable="true"
              clear-icon="mdi-close"
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="parentFilter"
              :items="parentDepartmentsForFilter"
              label="تصفية بالقسم الرئيسي"
              item-title="name"
              item-value="id"
              :clearable="true"
              clear-icon="mdi-close"
              dense
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
      
      <!-- Departments Table -->
      <v-data-table
        :headers="headers"
        :items="filteredDepartments"
        :loading="loading"
        :items-per-page="perPage"
        color="primary"
        first-icon="mdi-chevron-left"
        last-icon="mdi-chevron-right"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        show-current-page
        show-first-last-page
        show-first-page
        show-last-page
        show-next-page
        show-previous-page
        class="elevation-1"
      
      >
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="item.status === 'active' ? 'success' : 'error'"
            text-color="white"
            size="small"
          >
            {{ item.status === 'active' ? 'نشط' : 'غير نشط' }}
          </v-chip>
        </template>
        
        <template #[`item.name`]="{ item }">
          <div class="d-flex align-center">
            <span :style="{ marginLeft: (item.level || 0) * 20 + 'px' }">
              <span v-if="item.level > 0" class="text-grey mr-1">└─</span>
              {{ item.name }}
            </span>
          </div>
        </template>

        <template #[`item.internal`]="{ item }">
          <v-chip
            :color="item.internal ? 'success' : 'info'"
            variant="outlined"
            size="small"
          >
            {{ item.internal ? 'داخلي' : 'خارجي' }}
          </v-chip>
        </template>

        <template #[`item.active`]="{ item }">
          <v-chip
            :color="item.active ? 'success' : 'error'"
            text-color="white"
            size="small"
          >
            {{ item.active ? 'نشط' : 'غير نشط' }}
          </v-chip>
        </template>
        
        <template #[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="hasPermission('admin.departments.update')"
            v-permission="PERMISSIONS.DEPARTMENTS_EDIT"
            icon
            variant="text"
            size="small"
            color="primary"
            @click="editDepartment(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="hasPermission('admin.departments.destroy')"
            v-permission="PERMISSIONS.DEPARTMENTS_DELETE"
            icon
            variant="text"
            size="small"
            color="error"
            @click="deleteDepartment(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table> 
   
    </v-card>
    
    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <div>
            <span>{{ isEditing ? 'تعديل القسم' : 'إضافة قسم جديد' }}</span>
            <div class="text-caption text-grey mt-1">
              يمكن إنشاء الأقسام في هيكل شجري - اتركوا القسم التابع له فارغاً لإنشاء قسم رئيسي
            </div>
          </div>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="editedItem.rrn"
              label="الرقم الإشاري"
              :rules="departmentRules.rrn"
              required
            ></v-text-field>
            
            <v-text-field
              v-model="editedItem.name"
              label="اسم القسم"
              :rules="departmentRules.name"
              required
            ></v-text-field>
            
            <v-select
              v-model="editedItem.parent_id"
              :items="hierarchicalDepartments"
              label="القسم التابع له (اختياري)"
              item-title="displayName"
              item-value="id"
              clearable
              :hint="editedItem.parent_id ? 'سيتم إنشاء هذا القسم تحت القسم المحدد' : 'سيتم إنشاء هذا القسم كقسم رئيسي'"
              persistent-hint
            ></v-select>
            
            <v-switch
              v-model="editedItem.internal"
              label="قسم داخلي"
              color="success"
              inset
            ></v-switch>

            <v-switch
              v-model="editedItem.active"
              label="قسم نشط"
              color="success"
              inset
            ></v-switch>

            <v-select
              v-model="editedItem.send_permissions"
              :items="activeDepartments"
              label="صلاحيات الإرسال (الأقسام المسموح الإرسال إليها)"
              item-title="name"
              item-value="id"
              multiple
              chips
              closable-chips
              :hint="editedItem.send_permissions.length > 0 ? 'يمكن لمستخدمي هذا القسم إرسال رسائل إلى الأقسام المحددة' : 'يمكن للمستخدمين إرسال رسائل لجميع الأقسام'"
              persistent-hint
            >
              <template v-slot:item="{ props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon>mdi-send</v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-select>
            
            <!-- Stamp Preview (only in edit mode) -->
            <div v-if="isEditing && editedItem.stamp_url" class="mb-4 stamp-preview">
              <v-label class="text-subtitle-2 mb-2">ختم القسم الحالي</v-label>
              <div class="d-flex justify-center">
                <v-img
                  :src="editedItem.stamp_url"
                  alt="ختم القسم"
                  max-width="150"
                  max-height="150"
                  class="border rounded"
                  contain
                >
                  <template v-slot:error>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-icon color="grey">mdi-image-broken</v-icon>
                      <span class="text-caption ml-2">لا يمكن تحميل الصورة</span>
                    </div>
                  </template>
                </v-img>
              </div>
            </div>
            
            <v-file-input
              v-model="editedItem.stamp"
              label="ختم القسم"
              prepend-icon="mdi-stamp"
              :show-size="true"
              messages="يمكن رفع أي نوع من الملفات"
            ></v-file-input>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="save"
          >
            حفظ
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            @click="close"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف القسم "{{ itemToDelete?.name }}"؟
          <br>
          <small class="text-grey">لا يمكن التراجع عن هذا الإجراء.</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="text"
            @click="confirmDelete"
          >
            حذف
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="deleteDialog = false"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import departmentService from '@/services/departmentService'
import { formValidation } from '@/assets/validations'
import { PERMISSIONS } from '@/utils/permissions'
import { showSuccessAlert, showErrorAlert } from '@/utils/alertUtils'
import { hasPermission, hasModule } from '@/utils/permissions'
export default {
  name: 'DepartmentsView',
  data() {
    return {
      search: '',
      parentFilter: null,
      loading: false,
      dialog: false,
      valid: false,
      isEditing: false,
      deleteDialog: false,
      itemToDelete: null,
      
      // Pagination
      page: 1,
      perPage: 15,
      totalItems: 0,
      
      headers: [
        { title: 'رقم المرجع', key: 'rrn', align: 'start' },
        { title: 'اسم القسم', key: 'name', align: 'start' },
        { title: 'القسم التابع له', key: 'parent_name', align: 'start' },
        { title: 'النوع', key: 'internal', align: 'center' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'تاريخ الإنشاء', key: 'created_at', align: 'center' },
        { title: 'الإجراءات', key: 'actions', sortable: false, align: 'center' }
      ],
      departments: [],
      editedItem: {
        id: null,
        rrn: '',
        name: '',
        parent_id: null,
        internal: true,
        active: true,
        send_permissions: [],
        stamp: null,
        stamp_url: null
      },
      defaultItem: {
        id: null,
        rrn: '',
        name: '',
        parent_id: null,
        internal: true,
        active: true,
        send_permissions: [],
        stamp: null,
        stamp_url: null
      },
      
      // Validation rules using centralized validation
      departmentRules: formValidation.getDepartmentFormRules()
    }
  },
  
  mounted() {
    this.fetchDepartments()
  },
  
  computed: {
    PERMISSIONS() {
      return PERMISSIONS
    },
    
    parentDepartments() {
      // Show all departments except the current one being edited (to prevent circular reference)
      if (this.isEditing && this.editedItem.id) {
        return this.departments.filter(d => d.id !== this.editedItem.id);
      }
      return this.departments;
    },

    hierarchicalDepartments() {
      // Show departments in hierarchy format for parent selection
      const availableDepartments = this.isEditing && this.editedItem.id
        ? this.departments.filter(d => d.id !== this.editedItem.id)
        : this.departments;
      
      return this.sortDepartmentsHierarchically(availableDepartments);
    },
    
    filteredDepartments() {
      let result = [...this.departments];
      
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(d => 
          d.rrn?.toLowerCase().includes(searchLower) ||
          d.name?.toLowerCase().includes(searchLower)
        );
      }
      
      if (this.parentFilter) {
        result = result.filter(d => d.parent_id === this.parentFilter);
      }
      
      // Sort to show tree structure: root departments first, then their children
      return this.sortDepartmentsHierarchically(result);
    },

    // Get all parent departments for the filter dropdown
    parentDepartmentsForFilter() {
      return this.departments.filter(d => !d.parent_id);
    },

    // Get active departments for send permissions
    activeDepartments() {
      // Filter out the current department being edited to prevent self-reference
      let activeDepts = this.departments.filter(d => d.active !== false);
      
      if (this.isEditing && this.editedItem.id) {
        activeDepts = activeDepts.filter(d => d.id !== this.editedItem.id);
      }
      
      return activeDepts;
    }
  },
  
  methods: {

    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    
    sortDepartmentsHierarchically(departments) {
      const result = [];
      const processed = new Set();
      
      // Function to add department and its children recursively
      const addDepartmentWithChildren = (dept, level = 0) => {
        if (processed.has(dept.id)) return;
        
        // Add visual indentation for hierarchy
        const departmentWithLevel = {
          ...dept,
          level,
          displayName: '  '.repeat(level) + (level > 0 ? '└─ ' : '') + dept.name
        };
        
        result.push(departmentWithLevel);
        processed.add(dept.id);
        
        // Find and add children
        const children = departments
          .filter(d => d.parent_id === dept.id)
          .sort((a, b) => a.name.localeCompare(b.name, 'ar'));
        
        children.forEach(child => {
          addDepartmentWithChildren(child, level + 1);
        });
      };
      
      // Start with root departments (no parent)
      const rootDepartments = departments
        .filter(d => !d.parent_id)
        .sort((a, b) => a.name.localeCompare(b.name, 'ar'));
      
      rootDepartments.forEach(dept => {
        addDepartmentWithChildren(dept);
      });
      
      // Add any orphaned departments (parent not found)
      departments.forEach(dept => {
        if (!processed.has(dept.id)) {
          addDepartmentWithChildren(dept);
        }
      });
      
      return result;
    },

    async fetchDepartments() {
      try {
        this.loading = true
        const params = {
          '_with_[sendPermissions]': '' // Include send permissions relationship
        }
        
        const response = await departmentService.getDepartments(params)
        
        if (response.success) {
          // First set the departments without parent names
          this.departments = response.data.map(dept => ({
            ...dept,
            stamp: null, // Always set stamp to null for v-file-input compatibility
          }))
          
          // Then add parent names after all departments are loaded
          this.departments = this.departments.map(dept => ({
            ...dept,
            parent_name: dept.parent_id ? 
              this.departments.find(d => d.id === dept.parent_id)?.name || 'غير محدد' : 
              '-'
          }))
          
          this.totalItems = response.data.total
          this.page = response.data.current_page
        } else {
          showErrorAlert(this.$store, { data: { message: 'فشل في تحميل بيانات الأقسام' } }, 'فشل في تحميل بيانات الأقسام')
        }
      } catch (error) {
        console.error('Error fetching departments:', error)
        showErrorAlert(this.$store, error, 'حدث خطأ أثناء تحميل بيانات الأقسام')
      } finally {
        this.loading = false
      }
    },
    
    openAddDialog() {
      this.isEditing = false;
      this.editedItem = { ...this.defaultItem };
      this.dialog = true;
    },
    
    editDepartment(item) {
      this.isEditing = true;
      
      // Handle send_permissions - try different possible field names from backend
      let sendPermissions = [];
      if (item.send_permissions_ids && Array.isArray(item.send_permissions_ids)) {
        sendPermissions = item.send_permissions_ids;
      } else if (item.send_permissions && Array.isArray(item.send_permissions)) {
        // If send_permissions contains objects, extract IDs
        if (item.send_permissions.length > 0 && typeof item.send_permissions[0] === 'object') {
          sendPermissions = item.send_permissions.map(sp => sp.id);
        } else {
          // If send_permissions contains IDs directly
          sendPermissions = item.send_permissions;
        }
      } else if (item.sendPermissions && Array.isArray(item.sendPermissions)) {
        sendPermissions = item.sendPermissions;
      }
      
      this.editedItem = { 
        ...item,
        stamp: null, // Always set stamp to null for file input, use stamp_url for preview
        send_permissions: sendPermissions // Ensure send_permissions is an array
      };
      this.dialog = true;
    },
    
    deleteDepartment(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        const response = await departmentService.deleteDepartment(this.itemToDelete.id);
        showSuccessAlert(this.$store, response, 'تم حذف القسم بنجاح');
        this.fetchDepartments(); // Refresh the list
        this.deleteDialog = false;
        this.itemToDelete = null;
      } catch (error) {
        console.error('Error deleting department:', error);
        showErrorAlert(this.$store, error, 'حدث خطأ أثناء حذف القسم');
      }
    },
    
    async save() {
      if (!this.$refs.form.validate()) return;
      
      try {
        this.loading = true;
        
        if (this.isEditing) {
          const response = await departmentService.updateDepartment(this.editedItem.id, this.editedItem);
          showSuccessAlert(this.$store, response, 'تم تحديث القسم بنجاح');
          this.fetchDepartments(); // Refresh the list
        } else {
          const response = await departmentService.createDepartment(this.editedItem);
          showSuccessAlert(this.$store, response, 'تم إضافة القسم بنجاح');
          this.fetchDepartments(); // Refresh the list
        }
        
        this.close();
      } catch (error) {
        console.error('Error saving department:', error);
        showErrorAlert(this.$store, error, 'حدث خطأ أثناء حفظ بيانات القسم');
      } finally {
        this.loading = false;
      }
    },
    
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.$refs.form?.reset();
      });
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('ar-LY');
    }
  }
}
</script>

<style scoped>
.departments-container {
  padding: 16px;
}

:deep(.v-data-table) {
  background-color: white;
}

:deep(.v-data-table-header) {
  background-color: #f5f5f5;
}

.stamp-preview {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.stamp-preview img {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Hierarchy styling */
.v-data-table :deep(td) {
  vertical-align: top;
}

.hierarchy-indent {
  font-family: monospace;
  color: #666;
}

/* Form styling */
.v-select :deep(.v-field__input) {
  font-family: inherit;
}

/* Better spacing for hierarchical select */
:deep(.v-list-item-title) {
  font-family: inherit;
  white-space: nowrap;
  overflow: visible;
}
</style> 